import { type MouseEvent, useEffect, useMemo, useState } from 'react';
import { useFetcher } from 'react-router';
import { useAuthenticityToken } from 'remix-utils/csrf/react';
import { z } from 'zod';
import { zx } from 'zodix';

import { FavoriteButton } from '~/components/favorites/favorite-button';
import { setUserGymFavorite } from '~/domain/user-gym-favorites/commands/set-user-gym-favorite.server';
import { getUserGymFavorites } from '~/domain/user-gym-favorites/queries/get-user-gym-favorites.server';
import { getSessionId, requirePermission } from '~/utils/auth.server';
import { validateCSRF } from '~/utils/csrf.server';

import { type Route } from './+types/favorite';

export async function loader({ request }: Route.LoaderArgs) {
  // Return empty array if not authenticated (no favorites if there is no user)
  if (!(await getSessionId(request))) {
    return [];
  }

  const { userId } = await requirePermission(request, 'GymProfileList');
  const userGymFavoritesResult = await getUserGymFavorites(userId);
  const favoriteGymIds = userGymFavoritesResult.unwrapOr([]);

  return favoriteGymIds;
}

export const action = async ({ request }: Route.ActionArgs) => {
  const { userId } = await requirePermission(request, 'GymProfile.SetFavorite');
  const formData = await request.formData();
  await validateCSRF(formData, request.headers);

  const { isFavorite, gymId } = await zx.parseForm(formData, {
    gymId: z.string(),
    isFavorite: zx.BoolAsString,
  });

  const result = await setUserGymFavorite({ userId, gymId, isFavorite });
  if (result.isErr()) {
    return { isFavorite: null, status: 'error', error: 'Error setting favorite gym.' };
  }

  return { isFavorite, status: 'success', error: null };
};

export const useSetFavorite = () => {
  const csrf = useAuthenticityToken();
  const fetcher = useFetcher<typeof action>();

  const setFavorite = (gymId: string, isFavorite: boolean) => {
    fetcher.submit(
      { csrf, gymId, isFavorite },
      {
        method: 'POST',
        action: `/gym/resources/favorite`,
      },
    );
  };

  return { fetcher, setFavorite };
};

export function GymCardFavoriteButton({
  gymId,
  onChange,
}: {
  gymId: string;
  onChange?: (gymId: string, isFavorite: boolean) => void;
}) {
  const {
    fetcher: { data: setFavoriteFetcherData, state },
    setFavorite,
  } = useSetFavorite();

  const [data, setData] = useState(setFavoriteFetcherData);
  const { data: getFavoriteFetcherData } = useFetcher<typeof loader>({ key: 'favorite-gym-ids' });
  const isFavorite = useMemo(
    () => Boolean(getFavoriteFetcherData?.includes(gymId)),
    [getFavoriteFetcherData, gymId],
  );

  const handleFavoriteClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setFavorite(gymId, !isFavorite);
  };

  useEffect(() => {
    if (state === 'idle') {
      setData(setFavoriteFetcherData);
    }
  }, [state, setFavoriteFetcherData]);

  useEffect(() => {
    if (data?.status === 'success') {
      setData(undefined);
      if (onChange) onChange(gymId, data.isFavorite === true);
    }
  }, [data, onChange, gymId]);

  return <FavoriteButton size="icon-sm" highlighted={isFavorite} onClick={handleFavoriteClick} />;
}
